import { useEffect, useState } from 'react';

interface Props {
  label?: string;
  placeholder?: string;
  loading: boolean;
  defaultUrl?: string;
  initialName?: string;
  initialEmail?: string;
  initialBusinessName?: string;
  onSubmit: (payload: { name: string; email: string; businessName: string; url: string }) => void;
}

export default function UrlAnalyzeForm({
  label = 'Website or page URL',
  placeholder = 'https://example.com',
  loading,
  defaultUrl = '',
  initialName = '',
  initialEmail = '',
  initialBusinessName = '',
  onSubmit,
}: Props) {
  const [step, setStep] = useState<1 | 2>(1);
  const [name, setName] = useState(initialName ?? '');
  const [email, setEmail] = useState(initialEmail ?? '');
  const [businessName, setBusinessName] = useState(initialBusinessName ?? '');
  const [url, setUrl] = useState(defaultUrl);
  const [error, setError] = useState('');
  const MAX_ALLOWED_TRIES = 4;

  useEffect(() => {
  setName(typeof initialName === 'string' ? initialName : '');
}, [initialName]);

useEffect(() => {
  setEmail(typeof initialEmail === 'string' ? initialEmail : '');
}, [initialEmail]);

useEffect(() => {
  setBusinessName(typeof initialBusinessName === 'string' ? initialBusinessName : '');
}, [initialBusinessName]);

  const canContinueStep1 =
    name.trim().length > 0 &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()) &&
    businessName.trim().length > 0;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedName = name.trim();
    const trimmedEmail = email.trim();
    const trimmedBusinessName = businessName.trim();
    const trimmed = url.trim();
    if (!trimmedName) {
      setError('Please enter your name.');
      return;
    }
    if (!trimmedEmail) {
      setError('Please enter your email.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      setError('Please enter a valid email address.');
      return;
    }
    if (!trimmedBusinessName) {
      setError('Please enter your business name.');
      return;
    }
    if (!trimmed) {
      setError('Please enter a URL.');
      return;
    }
    if (!/^https?:\/\/.+/.test(trimmed)) {
      setError('URL must start with http:// or https://');
      return;
    }
    setError('');
    onSubmit({
      name: trimmedName,
      email: trimmedEmail,
      businessName: trimmedBusinessName,
      url: trimmed,
    });
  };

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', gap: '12px', flexDirection: 'column' }}>
      <p style={{ margin: 0, fontSize: '13px', color: 'var(--asneris-muted)', fontWeight: 600 }}>
        Step {step} of 2
      </p>

      {step === 1 && (
        <>
          <div className="asneris-field" style={{ minWidth: 280 }}>
            <label className="asneris-field-label" htmlFor="url-analyze-name">Step 1: Name</label>
            <input
              id="url-analyze-name"
              className="asneris-input"
              type="text"
              placeholder="Your full name"
              value={name}
              onChange={(e) => { setName(e.target.value); if (error) setError(''); }}
              disabled={loading}
            />
          </div>

          <div className="asneris-field" style={{ minWidth: 280 }}>
            <label className="asneris-field-label" htmlFor="url-analyze-email">Email</label>
            <input
              id="url-analyze-email"
              className="asneris-input"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => { setEmail(e.target.value); if (error) setError(''); }}
              disabled={loading}
            />
          </div>

          <div className="asneris-field" style={{ minWidth: 280 }}>
            <label className="asneris-field-label" htmlFor="url-analyze-business">Business Name</label>
            <input
              id="url-analyze-business"
              className="asneris-input"
              type="text"
              placeholder="Your business name"
              value={businessName}
              onChange={(e) => { setBusinessName(e.target.value); if (error) setError(''); }}
              disabled={loading}
            />
          </div>

          {error && (
            <span id="url-analyze-error" className="asneris-field-helper asneris-field-helper--error">{error}</span>
          )}

          <button
            type="button"
            className="asneris-btn asneris-btn-primary"
            disabled={loading || !canContinueStep1}
            style={{ alignSelf: 'flex-start', height: '48px', minWidth: '130px' }}
            onClick={() => {
              if (!canContinueStep1) {
                setError('Please complete Name, Email, and Business Name.');
                return;
              }
              setError('');
              setStep(2);
            }}
          >
            Continue to Step 2
          </button>
        </>
      )}

      {step === 2 && (
        <>
          <div className="asneris-field" style={{ minWidth: 280 }}>
            <label className="asneris-field-label" htmlFor="url-analyze-input">Step 2: {label}</label>
            <div className="asneris-input-wrap">
              <svg className="asneris-input-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
                <line x1="16.5" y1="16.5" x2="21" y2="21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
              <input
                id="url-analyze-input"
                className={`asneris-input asneris-input--with-icon${error ? ' asneris-input--error' : ''}`}
                type="url"
                placeholder={placeholder}
                value={url}
                onChange={(e) => { setUrl(e.target.value); if (error) setError(''); }}
                disabled={loading}
                aria-label={label}
                aria-describedby={error ? 'url-analyze-error' : undefined}
              />
            </div>
          </div>

          <p style={{ margin: 0, fontSize: '12px', color: 'var(--asneris-muted)' }}>
            Max allowed tries: <strong>{MAX_ALLOWED_TRIES}</strong>
          </p>

          {error && (
            <span id="url-analyze-error" className="asneris-field-helper asneris-field-helper--error">{error}</span>
          )}

          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <button
              type="button"
              className="asneris-btn asneris-btn-secondary"
              disabled={loading}
              style={{ height: '48px', minWidth: '130px' }}
              onClick={() => {
                setError('');
                setStep(1);
              }}
            >
              Back
            </button>
            <button
              type="submit"
              className="asneris-btn asneris-btn-primary"
              disabled={loading}
              style={{ height: '48px', minWidth: '130px' }}
            >
              {loading ? 'Analyzing…' : 'Analyze'}
            </button>
          </div>
        </>
      )}
    </form>
  );
}
